import React, { useState, useEffect } from 'react';
import { departmentAPI, projectAPI, taskAPI } from './api';

const VisualNextSystem = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedProj, setSelectedProj] = useState(null);
  const [status, setStatus] = useState('Loading...');
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newProjectClient, setNewProjectClient] = useState('');
  const [newProjectTask, setNewProjectTask] = useState('');
  const [newProjectDueDate, setNewProjectDueDate] = useState('');
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [showEditTask, setShowEditTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskText, setEditTaskText] = useState('');
  const [editTaskDueDate, setEditTaskDueDate] = useState('');
  const [showEditProject, setShowEditProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editProjectClient, setEditProjectClient] = useState('');
  const [editProjectTask, setEditProjectTask] = useState('');
  const [editProjectDueDate, setEditProjectDueDate] = useState('');
  const [showDeleteMode, setShowDeleteMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [showDeleteDepartment, setShowDeleteDepartment] = useState(false);
  const [showEditDepartment, setShowEditDepartment] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editDepartmentName, setEditDepartmentName] = useState('');

  // Load data from API on startup
  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const data = await departmentAPI.getAll();
      setDepartments(data);
      setStatus('Click any blue circle to start!');
    } catch (error) {
      console.error('Error loading departments:', error);
      setStatus('Error loading data. Make sure your backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectHover = (project, event) => {
    setHoveredProject(project);
    setHoverPosition({ x: event.clientX, y: event.clientY });
  };

  const handleProjectLeave = () => {
    setHoveredProject(null);
  };

  // Helper function to format dates nicely
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Due Today';
    if (diffDays === 1) return 'Due Tomorrow';
    if (diffDays === -1) return 'Due Yesterday';
    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''}`;
    if (diffDays <= 7) return `Due in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    
    return `Due ${date.toLocaleDateString()}`;
  };

  // Helper function to get date color based on urgency
  const getDateColor = (dateString) => {
    if (!dateString) return '#666';
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return '#f44336'; // Overdue - red
    if (diffDays === 0) return '#ff9800'; // Today - orange
    if (diffDays <= 3) return '#ff9800'; // Soon - orange
    return '#666'; // Normal - gray
  };

  const addDepartment = async () => {
    if (newDepartmentName.trim()) {
      try {
        await departmentAPI.create(newDepartmentName.trim());
        setStatus(`✅ Added department: "${newDepartmentName.trim()}"`);
        setNewDepartmentName('');
        setShowAddDepartment(false);
        loadDepartments(); // Refresh data
      } catch (error) {
        console.error('Error adding department:', error);
        setStatus('❌ Error adding department');
      }
    }
  };

  const deleteDepartment = async (deptId) => {
    try {
      await departmentAPI.delete(deptId);
      setStatus('✅ Department deleted');
      setShowDeleteDepartment(false);
      
      // If we were viewing the deleted department, go back to dashboard
      if (selectedDept && selectedDept.id === deptId) {
        setCurrentView('dashboard');
        setSelectedDept(null);
        setSelectedProj(null);
      }
      
      loadDepartments(); // Refresh data
    } catch (error) {
      console.error('Error deleting department:', error);
      setStatus('❌ Error deleting department');
    }
  };

  const startEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setEditDepartmentName(dept.name);
    setShowEditDepartment(true);
  };

  const saveEditDepartment = async () => {
    if (editDepartmentName.trim() && editingDepartment) {
      try {
        await departmentAPI.update(editingDepartment.id, editDepartmentName.trim());
        setStatus(`✅ Department renamed to: "${editDepartmentName.trim()}"`);
        setShowEditDepartment(false);
        setEditingDepartment(null);
        setEditDepartmentName('');
        loadDepartments(); // Refresh data
      } catch (error) {
        console.error('Error updating department:', error);
        setStatus('❌ Error updating department');
      }
    }
  };

  const cancelEditDepartment = () => {
    setShowEditDepartment(false);
    setEditingDepartment(null);
    setEditDepartmentName('');
  };

  const addProject = async (deptId) => {
    if (newProjectClient.trim() && newProjectTask.trim()) {
      try {
        await projectAPI.create(deptId, newProjectClient.trim(), newProjectTask.trim(), newProjectDueDate || null);
        setStatus(`✅ Added project: "${newProjectClient.trim()} - ${newProjectTask.trim()}"`);
        setNewProjectClient('');
        setNewProjectTask('');
        setNewProjectDueDate('');
        setShowAddProject(false);
        loadDepartments(); // Refresh data
      } catch (error) {
        console.error('Error adding project:', error);
        setStatus('❌ Error adding project');
      }
    }
  };

  const addTask = async (projId) => {
    if (newTaskText.trim()) {
      try {
        await taskAPI.create(projId, newTaskText.trim(), 'mine', newTaskDueDate || null);
        setStatus(`✅ Added task: "${newTaskText.trim()}"`);
        setNewTaskText('');
        setNewTaskDueDate('');
        setShowAddTask(false);
        loadDepartments(); // Refresh data
      } catch (error) {
        console.error('Error adding task:', error);
        setStatus('❌ Error adding task');
      }
    }
  };

  const toggleTaskStatus = async (taskId) => {
    try {
      await taskAPI.toggleStatus(taskId);
      setStatus('Task status updated');
      loadDepartments(); // Refresh data
    } catch (error) {
      console.error('Error toggling task status:', error);
      setStatus('❌ Error updating task status');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await taskAPI.delete(taskId);
      setStatus('Task deleted');
      loadDepartments(); // Refresh data
    } catch (error) {
      console.error('Error deleting task:', error);
      setStatus('❌ Error deleting task');
    }
  };

  const startEditTask = (task) => {
    setEditingTask(task);
    setEditTaskText(task.text);
    setEditTaskDueDate(task.dueDate || '');
    setShowEditTask(true);
  };

  const saveEditTask = async () => {
    if (editTaskText.trim() && editingTask) {
      try {
        await taskAPI.update(editingTask.id, editTaskText.trim(), editingTask.status, editTaskDueDate || null);
        setStatus(`✅ Task updated: "${editTaskText.trim()}"`);
        setShowEditTask(false);
        setEditingTask(null);
        setEditTaskText('');
        setEditTaskDueDate('');
        loadDepartments(); // Refresh data
      } catch (error) {
        console.error('Error updating task:', error);
        setStatus('❌ Error updating task');
      }
    }
  };

  const cancelEditTask = () => {
    setShowEditTask(false);
    setEditingTask(null);
    setEditTaskText('');
    setEditTaskDueDate('');
  };

  const startEditProject = (project) => {
    setEditingProject(project);
    setEditProjectClient(project.client || '');
    setEditProjectTask(project.task || '');
    setEditProjectDueDate(project.dueDate || '');
    setShowEditProject(true);
  };

  const saveEditProject = async () => {
    if (editProjectClient.trim() && editProjectTask.trim() && editingProject) {
      try {
        await projectAPI.update(editingProject.id, editProjectClient.trim(), editProjectTask.trim(), editProjectDueDate || null);
        setStatus(`✅ Project updated: "${editProjectClient.trim()} - ${editProjectTask.trim()}"`);
        setShowEditProject(false);
        setEditingProject(null);
        setEditProjectClient('');
        setEditProjectTask('');
        setEditProjectDueDate('');
        loadDepartments(); // Refresh data
      } catch (error) {
        console.error('Error updating project:', error);
        setStatus('❌ Error updating project');
      }
    }
  };

  const cancelEditProject = () => {
    setShowEditProject(false);
    setEditingProject(null);
    setEditProjectClient('');
    setEditProjectTask('');
    setEditProjectDueDate('');
  };

  const deleteProject = async (projId) => {
    try {
      await projectAPI.delete(projId);
      setStatus('✅ Project deleted');
      setShowDeleteConfirm(false);
      setProjectToDelete(null);
      setShowDeleteMode(false);
      loadDepartments(); // Refresh data
    } catch (error) {
      console.error('Error deleting project:', error);
      setStatus('❌ Error deleting project');
    }
  };

  const confirmDelete = (project) => {
    setProjectToDelete(project);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setProjectToDelete(null);
  };

  const getMyTaskCount = (dept) => {
    return dept.projects.reduce((total, project) => 
      total + project.tasks.filter(task => task.status === 'mine').length, 0
    );
  };

  const getWaitingTaskCount = (dept) => {
    return dept.projects.reduce((total, project) => 
      total + project.tasks.filter(task => task.status === 'waiting').length, 0
    );
  };

  const handleDeptClick = (dept) => {
    setSelectedDept(dept);
    setCurrentView('department');
    setStatus(`Viewing ${dept.name} department`);
  };

  const handleProjectClick = (project) => {
    setSelectedProj(project);
    setCurrentView('project');
    setStatus(`Viewing ${project.client} - ${project.task} project`);
  };

  const goBack = () => {
    if (currentView === 'project') {
      setCurrentView('department');
      setSelectedProj(null);
      setStatus(`Back to ${selectedDept.name} department`);
    } else if (currentView === 'department') {
      setCurrentView('dashboard');
      setSelectedDept(null);
      setShowDeleteMode(false);
      setStatus('Back to dashboard');
    }
  };

  // Helper function to calculate project positions in expanding circles
  const getProjectPosition = (projectIndex, totalProjects) => {
    if (totalProjects <= 4) {
      // For 4 or fewer projects, use the original pattern (N, E, S, W)
      const angle = (projectIndex * 90) * (Math.PI / 180);
      const radius = 90;
      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius
      };
    } else if (totalProjects <= 8) {
      // For 5-8 projects, arrange in a circle
      const angle = (projectIndex * (360 / totalProjects)) * (Math.PI / 180);
      const radius = 90;
      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius
      };
    } else {
      // For more than 8 projects, use multiple rings
      let currentIndex = projectIndex;
      let ring = 0;
      let projectsInRing = 8;
      
      // First ring has 8 projects
      if (currentIndex < 8) {
        ring = 0;
      } else {
        currentIndex -= 8;
        ring = 1;
        
        // Each additional ring can hold more projects
        while (currentIndex >= (12 + ring * 4)) {
          currentIndex -= (12 + ring * 4);
          ring++;
        }
        
        projectsInRing = ring === 1 ? 12 : (12 + (ring - 1) * 4);
      }
      
      const angle = (currentIndex * (360 / projectsInRing)) * (Math.PI / 180);
      const radius = 90 + (ring * 35);
      
      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius
      };
    }
  };

  // Show loading screen while data is being fetched
  if (loading) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{textAlign: 'center'}}>
          <h2>Loading Visual Next System...</h2>
          <p>Connecting to database...</p>
        </div>
      </div>
    );
  }

  // PROJECT VIEW
  if (currentView === 'project' && selectedProj) {
    return (
      <div style={{padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh'}}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <div style={{backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '10px', marginBottom: '20px'}}>
            <div style={{color: '#1565c0', fontWeight: 'bold'}}>📍 {status}</div>
          </div>
          
          <div style={{
            backgroundColor: '#2196f3',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px',
            display: 'inline-block'
          }} onClick={goBack}>
            ← Back to {selectedDept.name}
          </div>
          
          <h1 style={{fontSize: '2.5em', marginBottom: '10px'}}>
            {selectedProj.client} - {selectedProj.task}
          </h1>
          
          {selectedProj.dueDate && (
            <div style={{
              fontSize: '1.2em',
              color: getDateColor(selectedProj.dueDate),
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              Project {formatDate(selectedProj.dueDate)}
            </div>
          )}
          
          <div style={{
            backgroundColor: '#4caf50',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px',
            display: 'inline-block'
          }} onClick={() => setShowAddTask(true)}>
            + Add Task
          </div>
          
          {showAddTask && (
            <div style={{
              backgroundColor: '#f0f0f0',
              padding: '20px',
              borderRadius: '5px',
              marginBottom: '20px',
              border: '2px solid #4caf50'
            }}>
              <h3 style={{marginBottom: '10px'}}>Add New Task</h3>
              <input
                type="text"
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                placeholder="Enter task description..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <input
                type="date"
                value={newTaskDueDate}
                onChange={(e) => setNewTaskDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <div style={{display: 'flex', gap: '10px'}}>
                <div style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }} onClick={() => addTask(selectedProj.id)}>
                  Add Task
                </div>
                <div style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }} onClick={() => {
                  setShowAddTask(false);
                  setNewTaskText('');
                  setNewTaskDueDate('');
                }}>
                  Cancel
                </div>
              </div>
            </div>
          )}
          
          {showEditTask && editingTask && (
            <div style={{
              backgroundColor: '#f0f0f0',
              padding: '20px',
              borderRadius: '5px',
              marginBottom: '20px',
              border: '2px solid #ff9800'
            }}>
              <h3 style={{marginBottom: '10px'}}>Edit Task</h3>
              <input
                type="text"
                value={editTaskText}
                onChange={(e) => setEditTaskText(e.target.value)}
                placeholder="Enter task description..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <input
                type="date"
                value={editTaskDueDate}
                onChange={(e) => setEditTaskDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <div style={{display: 'flex', gap: '10px'}}>
                <div style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }} onClick={saveEditTask}>
                  Save Changes
                </div>
                <div style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }} onClick={cancelEditTask}>
                  Cancel
                </div>
              </div>
            </div>
          )}

          <div style={{backgroundColor: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'}}>
            <h2 style={{marginBottom: '20px'}}>Tasks:</h2>
            
            {selectedProj.tasks.length === 0 ? (
              <div style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                <div style={{fontSize: '3em', marginBottom: '10px'}}>📋</div>
                <p>No tasks yet. Click "Add Task" to get started!</p>
              </div>
            ) : (
              selectedProj.tasks.map(task => (
                <div key={task.id} style={{
                  padding: '15px',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      backgroundColor: task.status === 'mine' ? '#f44336' : '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }} onClick={() => toggleTaskStatus(task.id)}>
                      <div style={{width: '12px', height: '12px', backgroundColor: 'white', borderRadius: '50%'}}></div>
                    </div>
                    <div>
                      <div style={{fontWeight: 'bold', color: task.status === 'waiting' ? '#666' : '#000'}}>
                        {task.text}
                      </div>
                      <div style={{fontSize: '0.8em', color: '#666'}}>
                        {task.status === 'mine' ? 'Your action' : 'Waiting on others'}
                        {task.dueDate && (
                          <span style={{color: getDateColor(task.dueDate), marginLeft: '10px', fontWeight: 'bold'}}>
                            • {formatDate(task.dueDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{display: 'flex', gap: '10px'}}>
                    <div style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }} onClick={() => startEditTask(task)}>
                      Edit
                    </div>
                    <div style={{
                      backgroundColor: '#f44336',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }} onClick={() => deleteTask(task.id)}>
                      Delete
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // DEPARTMENT VIEW
  if (currentView === 'department' && selectedDept) {
    return (
      <div style={{padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh'}}>
        <div style={{maxWidth: '1000px', margin: '0 auto'}}>
          <div style={{backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '10px', marginBottom: '20px'}}>
            <div style={{color: '#1565c0', fontWeight: 'bold'}}>📍 {status}</div>
          </div>
          
          <div style={{
            backgroundColor: '#2196f3',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px',
            display: 'inline-block'
          }} onClick={goBack}>
            ← Back to Dashboard
          </div>
          
          <h1 style={{fontSize: '2.5em', marginBottom: '30px'}}>{selectedDept.name}</h1>
          
          <div style={{
            backgroundColor: '#4caf50',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'inline-block',
            marginBottom: '30px'
          }} onClick={() => setShowAddProject(true)}>
            + Add Project
          </div>
          
          {showAddProject && (
            <div style={{
              backgroundColor: '#f0f0f0',
              padding: '20px',
              borderRadius: '5px',
              marginBottom: '20px',
              border: '2px solid #4caf50'
            }}>
              <h3 style={{marginBottom: '15px', color: '#4caf50'}}>Add New Project</h3>
              <input
                type="text"
                value={newProjectClient}
                onChange={(e) => setNewProjectClient(e.target.value)}
                placeholder="Enter client name..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <input
                type="text"
                value={newProjectTask}
                onChange={(e) => setNewProjectTask(e.target.value)}
                placeholder="Enter task description..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <input
                type="date"
                value={newProjectDueDate}
                onChange={(e) => setNewProjectDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <div style={{display: 'flex', gap: '10px'}}>
                <div style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }} onClick={() => addProject(selectedDept.id)}>
                  Add Project
                </div>
                <div style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }} onClick={() => {
                  setShowAddProject(false);
                  setNewProjectClient('');
                  setNewProjectTask('');
                  setNewProjectDueDate('');
                }}>
                  Cancel
                </div>
              </div>
            </div>
          )}

          {showEditProject && editingProject && (
            <div style={{
              backgroundColor: '#fff3cd',
              padding: '20px',
              borderRadius: '5px',
              marginBottom: '20px',
              border: '2px solid #ff9800'
            }}>
              <h3 style={{marginBottom: '15px', color: '#ff9800'}}>Edit Project</h3>
              <p style={{marginBottom: '15px', color: '#666'}}>
                Editing: <strong>{editingProject.client} - {editingProject.task}</strong>
              </p>
              <input
                type="text"
                value={editProjectClient}
                onChange={(e) => setEditProjectClient(e.target.value)}
                placeholder="Enter client name..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <input
                type="text"
                value={editProjectTask}
                onChange={(e) => setEditProjectTask(e.target.value)}
                placeholder="Enter task description..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '10px'
                }}
              />
              <input
                type="date"
                value={editProjectDueDate}
                onChange={(e) => setEditProjectDueDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '20px'
                }}
              />
              <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                <div style={{
                  backgroundColor: '#ccc',
                  color: '#333',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={cancelEditProject}>
                  Cancel
                </div>
                <div style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={saveEditProject}>
                  Save Changes
                </div>
              </div>
            </div>
          )}

          {/* Project Delete Confirmation Dialog */}
          {showDeleteConfirm && projectToDelete && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '10px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                maxWidth: '400px',
                width: '90%'
              }}>
                <h3 style={{marginBottom: '15px', color: '#d32f2f'}}>Delete Project</h3>
                <p style={{marginBottom: '15px', lineHeight: '1.5'}}>
                  Are you sure you want to delete <strong>"{projectToDelete.client} - {projectToDelete.task}"</strong>?
                </p>
                <p style={{marginBottom: '20px', color: '#666', fontSize: '14px'}}>
                  This will permanently delete the project and all tasks. This action cannot be undone.
                </p>
                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <div style={{
                    backgroundColor: '#ccc',
                    color: '#333',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }} onClick={cancelDelete}>
                    Cancel
                  </div>
                  <div style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }} onClick={() => deleteProject(projectToDelete.id)}>
                    Delete Project
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px'}}>
            {selectedDept.projects.map(project => (
              <div key={project.id} style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '10px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                border: '1px solid #eee'
              }}>
                <h3 style={{ marginBottom: '15px', fontSize: '1.3em' }}>
                  {project.client} - {project.task}
                </h3>
                
                {project.dueDate && (
                  <div style={{
                    fontSize: '0.9em',
                    color: getDateColor(project.dueDate),
                    fontWeight: 'bold',
                    marginBottom: '10px'
                  }}>
                    {formatDate(project.dueDate)}
                  </div>
                )}
                
                <div style={{marginBottom: '15px'}}>
                  {project.tasks.map(task => (
                    <div key={task.id} style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'}}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: task.status === 'mine' ? '#f44336' : '#4caf50'
                      }}></div>
                      <span style={{fontSize: '0.9em'}}>{task.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* Action buttons */}
                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                  <div style={{
                    backgroundColor: '#2196f3',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    flex: '1',
                    textAlign: 'center',
                    minWidth: '80px'
                  }} onClick={() => handleProjectClick(project)}>
                    View Tasks
                  </div>
                  
                  <div style={{
                    backgroundColor: '#ff9800',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }} onClick={() => startEditProject(project)}>
                    Edit
                  </div>
                  
                  <div style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }} onClick={() => confirmDelete(project)}>
                    Delete
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // MAIN DASHBOARD
  return (
    <div style={{padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh'}}>
      <div style={{maxWidth: '1200px', margin: '0 auto'}}>
        
        <div style={{backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '10px', marginBottom: '30px'}}>
          <div style={{color: '#1565c0', fontWeight: 'bold'}}>📍 {status}</div>
        </div>
        
        <div style={{textAlign: 'center', marginBottom: '50px'}}>
          <h1 style={{fontSize: '3em', marginBottom: '10px', color: '#333'}}>Protasker</h1>
          <p style={{fontSize: '1.2em', color: '#666'}}>Click any blue circle to explore your projects</p>
          
          <div style={{display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px'}}>
            <div 
              style={{
                backgroundColor: '#4caf50',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }} 
              onClick={() => setShowAddDepartment(true)}
            >
              + Add Department
            </div>
            
            <div 
              style={{
                backgroundColor: '#ff9800',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }} 
              onClick={() => setShowEditDepartment(true)}
            >
              Edit Department
            </div>
            
            <div 
              style={{
                backgroundColor: '#f44336',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }} 
              onClick={() => setShowDeleteDepartment(true)}
            >
              Delete Department
            </div>
          </div>
        </div>

        {/* Add Department Dialog */}
        {showAddDepartment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%'
            }}>
              <h3 style={{marginBottom: '15px', color: '#4caf50'}}>Add New Department</h3>
              <input
                type="text"
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
                placeholder="Enter department name..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '20px'
                }}
              />
              <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                <div style={{
                  backgroundColor: '#ccc',
                  color: '#333',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={() => {
                  setShowAddDepartment(false);
                  setNewDepartmentName('');
                }}>
                  Cancel
                </div>
                <div style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={addDepartment}>
                  Add Department
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Department Dialog */}
        {showEditDepartment && !editingDepartment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%'
            }}>
              <h3 style={{marginBottom: '15px', color: '#ff9800'}}>Select Department to Edit</h3>
              <div style={{marginBottom: '20px'}}>
                {departments.map(dept => (
                  <div key={dept.id} style={{
                    padding: '10px',
                    backgroundColor: '#f5f5f5',
                    marginBottom: '10px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    border: '1px solid #ddd'
                  }} onClick={() => startEditDepartment(dept)}>
                    {dept.name}
                  </div>
                ))}
              </div>
              <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                <div style={{
                  backgroundColor: '#ccc',
                  color: '#333',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={() => setShowEditDepartment(false)}>
                  Cancel
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Department Name Dialog */}
        {showEditDepartment && editingDepartment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%'
            }}>
              <h3 style={{marginBottom: '15px', color: '#ff9800'}}>Edit Department Name</h3>
              <p style={{marginBottom: '15px', color: '#666'}}>
                Current name: <strong>{editingDepartment.name}</strong>
              </p>
              <input
                type="text"
                value={editDepartmentName}
                onChange={(e) => setEditDepartmentName(e.target.value)}
                placeholder="Enter new department name..."
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  marginBottom: '20px'
                }}
              />
              <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                <div style={{
                  backgroundColor: '#ccc',
                  color: '#333',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={cancelEditDepartment}>
                  Cancel
                </div>
                <div style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={saveEditDepartment}>
                  Save Changes
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Department Dialog */}
        {showDeleteDepartment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%'
            }}>
              <h3 style={{marginBottom: '15px', color: '#f44336'}}>Delete Department</h3>
              <p style={{marginBottom: '15px', color: '#666'}}>
                Select a department to delete. This will permanently delete the department and all its projects and tasks.
              </p>
              <div style={{marginBottom: '20px'}}>
                {departments.map(dept => (
                  <div key={dept.id} style={{
                    padding: '10px',
                    backgroundColor: '#ffebee',
                    marginBottom: '10px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    border: '2px solid #f44336',
                    color: '#d32f2f'
                  }} onClick={() => {
                    if (window.confirm(`Are you sure you want to delete "${dept.name}" and all its projects and tasks? This cannot be undone.`)) {
                      deleteDepartment(dept.id);
                    }
                  }}>
                    {dept.name} ({dept.projects.length} projects)
                  </div>
                ))}
              </div>
              <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                <div style={{
                  backgroundColor: '#ccc',
                  color: '#333',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }} onClick={() => setShowDeleteDepartment(false)}>
                  Cancel
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '80px',
          padding: '50px 0',
          minHeight: '400px'
        }}>
          {departments.map((dept, index) => (
            <div key={dept.id} style={{position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              
              <div
                onClick={() => handleDeptClick(dept)}
                style={{
                  width: index < 2 ? '140px' : '120px',
                  height: index < 2 ? '140px' : '120px',
                  backgroundColor: '#2196f3',
                  borderRadius: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 10px 30px rgba(33, 150, 243, 0.3)',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '20px'
                }}
              >
                <div style={{marginBottom: '10px'}}>{dept.name}</div>
                <div style={{fontSize: '14px', opacity: '0.9'}}>
                  <span style={{color: '#ffcdd2'}}>{getMyTaskCount(dept)}</span>
                  <span style={{margin: '0 5px'}}>•</span>
                  <span style={{color: '#c8e6c9'}}>{getWaitingTaskCount(dept)}</span>
                </div>
              </div>

              {dept.projects.map((project, projIndex) => {
                const position = getProjectPosition(projIndex, dept.projects.length);
                
                const client = project.client || 'Unknown';
                const task = project.task || 'Task';
                const firstTaskWord = task.split(' ')[0];
                
                return (
                  <div
                    key={project.id}
                    onClick={() => {
                      setSelectedDept(dept);
                      handleProjectClick(project);
                    }}
                    onMouseEnter={(e) => handleProjectHover(project, e)}
                    onMouseLeave={handleProjectLeave}
                    style={{
                      position: 'absolute',
                      left: `calc(50% + ${position.x}px - 30px)`,
                      top: `calc(50% + ${position.y}px - 30px)`,
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#666',
                      borderRadius: '50%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                      transition: 'all 0.2s ease',
                      padding: '4px'
                    }}
                  >
                    <div style={{ fontSize: '9px', lineHeight: '1' }}>
                      {client.length > 8 ? client.substring(0, 8) + '...' : client}
                    </div>
                    <div style={{ fontSize: '8px', lineHeight: '1', marginTop: '2px' }}>
                      {firstTaskWord.length > 6 ? firstTaskWord.substring(0, 6) + '...' : firstTaskWord}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Hover Tooltip for Projects */}
        {hoveredProject && (
          <div style={{
            position: 'fixed',
            left: `${hoverPosition.x + 15}px`,
            top: `${hoverPosition.y - 10}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
            maxWidth: '300px',
            fontSize: '14px',
            pointerEvents: 'none'
          }}>
            <div style={{fontWeight: 'bold', marginBottom: '8px', fontSize: '16px'}}>
              {hoveredProject.client} - {hoveredProject.task}
            </div>
            
            <div style={{marginBottom: '8px', color: '#ccc'}}>
              {hoveredProject.tasks.length} task{hoveredProject.tasks.length !== 1 ? 's' : ''}
            </div>
            
            {hoveredProject.tasks.length > 0 ? (
              <div>
                <div style={{marginBottom: '4px', fontSize: '12px', color: '#aaa'}}>Recent tasks:</div>
                {hoveredProject.tasks.slice(0, 3).map((task, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '4px',
                    fontSize: '12px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: task.status === 'mine' ? '#ff6b6b' : '#51cf66'
                    }}></div>
                    <div style={{flex: 1}}>
                      <span style={{color: task.status === 'mine' ? '#fff' : '#ccc'}}>
                        {task.text.length > 25 ? `${task.text.substring(0, 25)}...` : task.text}
                      </span>
                      {task.dueDate && (
                        <div style={{
                          fontSize: '10px',
                          color: getDateColor(task.dueDate),
                          marginTop: '2px',
                          fontWeight: 'bold'
                        }}>
                          {formatDate(task.dueDate)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {hoveredProject.tasks.length > 3 && (
                  <div style={{fontSize: '12px', color: '#aaa', marginTop: '4px'}}>
                    +{hoveredProject.tasks.length - 3} more task{hoveredProject.tasks.length - 3 !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ) : (
              <div style={{fontSize: '12px', color: '#aaa', fontStyle: 'italic'}}>
                No tasks yet
              </div>
            )}
          </div>
        )}

        <div style={{backgroundColor: 'white', padding: '30px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginTop: '50px'}}>
          <h2 style={{marginBottom: '20px', color: '#333'}}>🎉 Database Connected!</h2>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px'}}>
            <div>
              <h3 style={{color: '#2196f3', marginBottom: '10px'}}>🎯 Navigation</h3>
              <ul style={{color: '#666', lineHeight: '1.6'}}>
                <li>Click large blue circles = View department</li>
                <li>Click small gray circles = View project</li>
                <li>Hover over gray circles for project preview</li>
                <li>Use back buttons to navigate</li>
              </ul>
            </div>
            <div>
              <h3 style={{color: '#2196f3', marginBottom: '10px'}}>🔴 Colors</h3>
              <ul style={{color: '#666', lineHeight: '1.6'}}>
                <li>Red dots = Your actions</li>
                <li>Green dots = Waiting on others</li>
                <li>Click dots to toggle status</li>
              </ul>
            </div>
            <div>
              <h3 style={{color: '#2196f3', marginBottom: '10px'}}>💾 Data Storage</h3>
              <ul style={{color: '#666', lineHeight: '1.6'}}>
                <li>All data is now stored permanently in your database</li>
                <li>Changes are saved automatically</li>
                <li>Ready for deployment to your custom domain!</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <VisualNextSystem />
    </div>
  );
}

export default App;